/**
 * Decision result for resolving a deep-linked feed post.
 * - `in-list`: the target post is already loaded in the feed list, just open it.
 * - `fetch`: the target post is not in the loaded list, fetch it by id.
 * - `none`: there is no feedId to resolve.
 */
export type DeepLinkTarget =
  | { kind: 'in-list' }
  | { kind: 'fetch' }
  | { kind: 'none' }

/**
 * Decides how to resolve a deep-linked post given the URL feedId and the
 * currently loaded posts. This is pure logic so it can be unit-tested in
 * isolation from the FeedPage component's async/UI concerns.
 *
 * Typed against the minimal `{ id }` shape (rather than the full `Post`) so
 * this module stays free of cross-package imports — keeping its test fast and
 * immune to the cold cross-package module-load race in the parallel jest run.
 */
export const resolveDeepLinkTarget = (
  feedId: string | null | undefined,
  posts: { id: string }[]
): DeepLinkTarget => {
  if (!feedId) {
    return { kind: 'none' }
  }

  const inList = posts.some((post) => post.id === feedId)
  return inList ? { kind: 'in-list' } : { kind: 'fetch' }
}
